'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const klaviyoFormSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required'),
})

type FormValues = z.infer<typeof klaviyoFormSchema>

interface KlaviyoFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function KlaviyoForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: KlaviyoFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(klaviyoFormSchema),
    defaultValues: {
      apiKey: defaultValues.credentials?.apiKey ?? '',
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const credentials = data
    await onSubmit({ credentials })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">{/* Event Support Info */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('klaviyo.eventSupport')}</AlertTitle>
        <AlertDescription>
          <div className="space-y-2">
            <p className="text-sm">{t('klaviyo.eventsDescription')}</p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              <li>{t('klaviyo.eventCheckoutStarted')}</li>
              <li>{t('klaviyo.eventAbandonedCart')}</li>
              <li>{t('klaviyo.eventOrderPaid')}</li>
              <li>{t('klaviyo.eventSubscriptionStarted')}</li>
              <li>{t('klaviyo.eventSubscriptionCanceled')}</li>
              <li>{t('klaviyo.eventSubscriptionPaused')}</li>
              <li>{t('klaviyo.eventSubscriptionResumed')}</li>
              <li>{t('klaviyo.eventIncomingRebill')}</li>
              <li>{t('klaviyo.eventSubscriptionRenewed')}</li>
              <li>{t('klaviyo.eventClubActivated')}</li>
              <li>{t('klaviyo.eventPromoCodeCreated')}</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      {/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('klaviyo.apiKeyTitle')}</CardTitle>
          <CardDescription>
            {t('klaviyo.apiKeyDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">
              {t('klaviyo.apiKey')}
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder="pk_*"
              disabled={isLoading}
              {...register('apiKey')}
            />
            {errors.apiKey && (
              <p className="text-xs text-destructive">{t('klaviyo.apiKeyRequired')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('klaviyo.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-3 text-sm text-muted-foreground">
              <li>
                <span dangerouslySetInnerHTML={{ __html: t('klaviyo.setupStep1') }} />{' '}
                <a
                  href="https://www.klaviyo.com/settings/account/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('klaviyo.setupStep1Link')}
                </a>
                .
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('klaviyo.setupStep2') }} />
              <li>
                <span dangerouslySetInnerHTML={{ __html: t('klaviyo.setupStep3') }} />
                <ul className="list-inside list-disc pl-4">
                  <li dangerouslySetInnerHTML={{ __html: t('klaviyo.setupStep3Scope1') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('klaviyo.setupStep3Scope2') }} />
                </ul>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('klaviyo.setupStep4') }} />
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
